import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { normalizeError } from "../../lib/normalizeError";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { Database, Plus, Pencil, Trash2, MapPin, Building2, Truck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const TABS = [
  { key: "locations", label: "Locations", icon: MapPin, fields: [
    { key: "label", label: "Label", placeholder: "e.g. Shelf A-1" },
    { key: "descriptor", label: "Type / Descriptor", placeholder: "e.g. Shelf, Bin, Rack" },
  ]},
  { key: "departments", label: "Departments", icon: Building2, fields: [
    { key: "name", label: "Name", placeholder: "e.g. Machining, Assembly" },
  ]},
  { key: "suppliers", label: "Suppliers", icon: Truck, fields: [
    { key: "name", label: "Name", placeholder: "e.g. McMaster-Carr" },
    { key: "url", label: "URL", placeholder: "https://..." },
  ]},
];

export default function MasterData() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("locations");
  const [data, setData] = useState({ locations: [], departments: [], suppliers: [] });
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // { mode: "add"|"edit", tab, entry? }
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [loc, dep, sup] = await Promise.all([
        axios.get(`${API}/locations`),
        axios.get(`${API}/departments`),
        axios.get(`${API}/suppliers`),
      ]);
      setData({ locations: loc.data || [], departments: dep.data || [], suppliers: sup.data || [] });
    } catch (e) {
      toast.error("Failed to load master data");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const tabCfg = TABS.find(t => t.key === activeTab);
  const rows = data[activeTab] || [];

  const openAdd = () => {
    const empty = {};
    tabCfg.fields.forEach(f => { empty[f.key] = ""; });
    setForm(empty);
    setModal({ mode: "add", tab: activeTab });
  };

  const openEdit = (entry) => {
    const f = {};
    tabCfg.fields.forEach(fld => { f[fld.key] = entry[fld.key] || ""; });
    setForm(f);
    setModal({ mode: "edit", tab: activeTab, entry });
  };

  const handleSave = async () => {
    setBusy(true);
    try {
      if (modal.mode === "add") {
        await axios.post(`${API}/admin/${modal.tab}`, form);
        toast.success("Created");
      } else {
        await axios.put(`${API}/admin/${modal.tab}/${modal.entry.id}`, form);
        toast.success("Updated");
      }
      setModal(null);
      fetchAll();
    } catch (e) {
      toast.error(normalizeError(e.response?.data) || "Save failed");
    } finally { setBusy(false); }
  };

  const handleDelete = async (entry) => {
    if (!window.confirm(`Delete "${entry.name || entry.label}"?`)) return;
    try {
      await axios.delete(`${API}/admin/${activeTab}/${entry.id}`);
      toast.success("Deleted");
      fetchAll();
    } catch (e) {
      toast.error(normalizeError(e.response?.data) || "Delete failed");
    }
  };

  return (
    <div className="flex-1 flex flex-col">
      <div className="h-16 border-b border-border px-6 flex items-center justify-between bg-card">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 border border-primary/30 rounded-sm flex items-center justify-center">
            <Database className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="font-heading text-2xl tracking-tight">MASTER DATA</h1>
            <p className="text-xs text-muted-foreground">Locations, Departments, Suppliers</p>
          </div>
        </div>
        <Button onClick={openAdd} className="uppercase tracking-wider rounded-sm" data-testid="add-entry-btn">
          <Plus className="w-4 h-4 mr-1" /> Add {tabCfg?.label?.slice(0, -1)}
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
        <div className="border-b border-border px-6 bg-card">
          <TabsList className="h-12 bg-transparent border-0 p-0 gap-6">
            {TABS.map(t => {
              const Icon = t.icon;
              return (
                <TabsTrigger key={t.key} value={t.key}
                  className="h-12 px-0 border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-primary rounded-none bg-transparent uppercase tracking-wider text-sm"
                  data-testid={`tab-${t.key}`}>
                  <Icon className="w-4 h-4 mr-2" /> {t.label} ({(data[t.key] || []).length})
                </TabsTrigger>
              );
            })}
          </TabsList>
        </div>

        <div className="p-6 flex-1">
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : rows.length === 0 ? (
            <div className="text-center py-12">
              <Database className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No {tabCfg?.label?.toLowerCase()} yet</p>
              <Button onClick={openAdd} variant="outline" className="mt-4 uppercase tracking-wider rounded-sm">
                <Plus className="w-4 h-4 mr-1" /> Add First {tabCfg?.label?.slice(0, -1)}
              </Button>
            </div>
          ) : (
            <div className="industrial-card overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="industrial-table-header">
                    {tabCfg.fields.map(f => <TableHead key={f.key}>{f.label}</TableHead>)}
                    <TableHead className="w-28">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map(entry => (
                    <TableRow key={entry.id} className="industrial-table-row" data-testid={`row-${entry.id}`}>
                      {tabCfg.fields.map(f => (
                        <TableCell key={f.key} className={f.key === "url" ? "font-mono text-xs text-muted-foreground truncate max-w-[200px]" : ""}>
                          {entry[f.key] || "-"}
                        </TableCell>
                      ))}
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(entry)} data-testid={`edit-${entry.id}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400 hover:text-red-300" onClick={() => handleDelete(entry)} data-testid={`delete-${entry.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </Tabs>

      {/* Add/Edit Modal */}
      <Dialog open={!!modal} onOpenChange={open => !open && setModal(null)}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl tracking-tight">
              {modal?.mode === "add" ? "ADD" : "EDIT"} {tabCfg?.label?.slice(0, -1)?.toUpperCase()}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {tabCfg?.fields.map(f => (
              <div key={f.key} className="space-y-2">
                <Label className="text-xs uppercase text-muted-foreground">{f.label}</Label>
                <Input
                  value={form[f.key] || ""}
                  onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                  placeholder={f.placeholder}
                  className="industrial-input"
                  data-testid={`input-${f.key}`}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setModal(null)} className="uppercase tracking-wider rounded-sm">Cancel</Button>
            <Button onClick={handleSave} disabled={busy} className="uppercase tracking-wider rounded-sm" data-testid="save-entry-btn">
              {busy ? "..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
